import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import { StatusHistory, StepResult } from '../../../developerModules/model';

import axios from '../../../sharedModules/utils/axios.utils';

const ApprovalAutomationsSection: React.FC<{ backflowJobGroupId: string }> = ({ backflowJobGroupId }) => {
  const [history, setHistory] = useState<StatusHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const [retrySuccess, setRetrySuccess] = useState('');

  const fetchHistory = async () => {
    try {
      const response = await axios.get<any>('/status_callback_audits', {
        params: {
          'ransack[record_type_eq]': 'BackflowJobGroup',
          'ransack[record_id_eq]': backflowJobGroupId,
          'ransack[new_status_eq]': 'approved',
          per_page: 1,
        },
      });
      const histories = response.data.result ?? [];
      setHistory(histories.length > 0 ? histories[0] : null);
    } catch {
      setHistory(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    if (!history) return;
    setRetrying(true);
    setRetryError('');
    setRetrySuccess('');
    try {
      await axios.post(`/status_callback_audits/${history.id}/retry`);
      setRetrySuccess('Retry completed successfully');
      await fetchHistory();
    } catch {
      setRetryError('Retry failed. Please try again.');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [backflowJobGroupId]);

  if (loading) return null;
  if (!history) return null;

  const isStepFailed = (value: string | StepResult) => typeof value === 'object' && value.status === 'failed';
  const getStepError = (value: string | StepResult) => (typeof value === 'object' ? value.error : undefined);

  const stepEntries = Object.entries(history.step_results_titleized);

  return (
    <Grid item xs={6}>
      <Card elevation={3} sx={{ height: '100%' }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" fontWeight="600">
              Approval Automations
            </Typography>
            {history.success === true && (
              <Chip label="All Steps Passed" color="success" size="small" icon={<CheckCircleIcon />} />
            )}
            {history.success === false && (
              <Chip label="Some Steps Failed" color="error" size="small" icon={<ErrorIcon />} />
            )}
            {history.success === null && (
              <Chip label="In Progress" color="warning" size="small" icon={<HourglassEmptyIcon />} />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {retryError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRetryError('')}>
              {retryError}
            </Alert>
          )}
          {retrySuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRetrySuccess('')}>
              {retrySuccess}
            </Alert>
          )}

          {stepEntries.map(([stepName, value]) => (
            <Box
              key={stepName}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              sx={{ py: 1, borderBottom: '1px solid #eee' }}
            >
              <Typography variant="body2">{stepName}</Typography>
              {isStepFailed(value) ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label="Failed" color="error" size="small" />
                  {getStepError(value) && (
                    <Typography variant="caption" color="error">
                      {getStepError(value)}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Chip label="Success" color="success" size="small" />
              )}
            </Box>
          ))}

          {stepEntries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No steps recorded yet.
            </Typography>
          )}

          {history.success === false && (
            <Box mt={2}>
              <Button
                variant="contained"
                color="warning"
                startIcon={retrying ? <CircularProgress size={20} /> : <RefreshIcon />}
                onClick={handleRetry}
                disabled={retrying}
                fullWidth
              >
                {retrying ? 'Retrying...' : 'Retry Failed Steps'}
              </Button>
            </Box>
          )}

          {history.retry_count > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Retried {history.retry_count} time{history.retry_count !== 1 ? 's' : ''}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default ApprovalAutomationsSection;
